import http from '@/lib/http'
import { isUserTrialPrescriber } from '@/lib/permissions'
import { useUtilityStore } from '@/lib/stores/utils'
import { isMultiProductOrderMedRequest } from '@/lib/utils'
import type { Investigation, MedRequest } from '@/types/data'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useFlag } from '@unleash/proxy-client-react'
import { useCallback } from 'react'

export type CutoffTimes = Record<
  'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday',
  {
    is_delivering: boolean
    is_collecting: boolean
    cutoff_time: string
  }
>

export type TotalMedicationRequestResponse = {
  beforeCutOff: number
  total: number
  totalInvestigations: number
  cutoffTimes: CutoffTimes
}

export const useTotalMedicationRequests = () => {
  return useQuery<TotalMedicationRequestResponse>({
    queryKey: ['totalMedicationRequests'],
    queryFn: async () => {
      const result = (await http.get('/dashboard-totals', {
        timeout: (http.defaults.timeout ?? 10000) * 6
      })) as TotalMedicationRequestResponse

      // If the user is a trial prescriber, filter out medications by number.
      if (isUserTrialPrescriber()) {
        const { actionedMedicationRequestIds, actionedInvestigationIds } = useUtilityStore.getState()
        return {
          ...result,
          total: result.total - actionedMedicationRequestIds.length,
          totalInvestigations: result.totalInvestigations - actionedInvestigationIds.length,
          beforeCutOff: result.beforeCutOff - actionedMedicationRequestIds.length
        }
      }

      return result
    },
    refetchInterval: 30000
  })
}

// With multi product orders off, an MPO's other items are dropped so it reviews on its own; MPS is untouched.
const useSelectMedRequest = () => {
  const isMultiProductOrdersEnabled = useFlag('multi-product-orders')

  return useCallback(
    (medRequest: MedRequest): MedRequest =>
      isMultiProductOrdersEnabled || !isMultiProductOrderMedRequest(medRequest)
        ? medRequest
        : { ...medRequest, orderMedRequests: undefined },
    [isMultiProductOrdersEnabled]
  )
}

export const useSuspenseMedicationRequestById = (id: string) => {
  const select = useSelectMedRequest()

  return useSuspenseQuery<MedRequest>({
    queryKey: ['medicationRequestById', id],
    queryFn: () => http.get(`/prescriber/med-requests/${id}`),
    select
  })
}

export const useMedicationRequestById = (id: string, enabled: boolean = true) => {
  const select = useSelectMedRequest()

  return useQuery<MedRequest>({
    queryKey: ['medicationRequestById', id],
    queryFn: () => http.get(`/prescriber/med-requests/${id}`),
    enabled: enabled && !!id,
    select
  })
}

export const useInvestigationRequestById = (id: string, enabled: boolean = true) => {
  return useQuery<Investigation>({
    queryKey: ['investigationRequestById', id],
    queryFn: () =>
      http.get(`/prescriber/investigations`, {
        params: {
          investigationId: id
        }
      }),
    enabled
  })
}

export type TimelineEntry = {
  note: string
  note_type: string
  created_at: string
  action_by_id?: string | null
  action_by_name?: string | null
}

export type TimelineResponse = {
  data: TimelineEntry[]
  total: number
}

export const useMedicationRequestTimeline = (hash: string, enabled: boolean = true) => {
  return useQuery<TimelineResponse>({
    queryKey: ['medicationRequestTimeline', hash],
    queryFn: () =>
      http.get(`/timelines/${hash}`, {
        params: { object_type: 'medication_request' }
      }),
    enabled: enabled && !!hash
  })
}

export const getModifiedNameFromTimeline = (entries: TimelineEntry[] | undefined): string | null => {
  const lastEntry = entries?.filter(entry => entry.note_type === 'status_update' && !!entry.action_by_name).at(-1)

  return lastEntry?.action_by_name ?? null
}
